import React, { useEffect, useState } from "react";
import { FileMinus2, ArrowUpRight } from "lucide-react";
import { Button } from "../../ui/button";
import { adminApi } from "../../../context/AuthContext";

const money = (n, cur) => `${cur} ${Number(n || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const CreditRelationsPanel = ({ inv, onOpenInvoice }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const isCredit = inv.document_type === "credit_note";
  useEffect(() => {
    if (isCredit) return;
    let active = true;
    setData(null);
    setError("");
    adminApi.get(`/admin/sales-invoices/${inv.id}/credit-notes`)
      .then(({ data: result }) => active && setData(result))
      .catch(() => active && setError("Creditnota’s konden niet worden geladen. Open de factuur opnieuw."));
    return () => { active = false; };
  }, [inv.id, inv.updated_at, isCredit]);

  if (isCredit) return (
    <section className="border-t pt-4 space-y-3 text-xs break-words" data-testid="credit-relation-panel-cn">
      <h4 className="font-bold flex gap-2 items-center"><FileMinus2 className="w-4 h-4" /> Originele factuur</h4>
      <Button variant="link" className="p-0 h-auto whitespace-normal text-left" onClick={() => onOpenInvoice(inv.original_invoice_id)} disabled={!inv.original_invoice_id} data-testid="credit-open-original">
        {inv.original_invoice_number || "Originele factuur"} <ArrowUpRight className="w-3 h-3 ml-1 shrink-0" />
      </Button>
      <p data-testid="credit-no-payment-notice">Geen betaling vereist. Deze creditnota bevestigt geen uitgevoerde terugbetaling.</p>
      {inv.credit_reason && <p data-testid="credit-reason-display">Reden: {inv.credit_reason}</p>}
      <details data-testid="credit-audit-history">
        <summary className="cursor-pointer font-bold" data-testid="credit-audit-toggle">Documenthistorie</summary>
        <ul className="space-y-2 mt-2">
          {(inv.document_events || []).map((e, i) => <li key={i} data-testid={`credit-audit-event-${i}`}>
            <span className="font-medium">{{ credit_created: "Aangemaakt", pdf_exported: "PDF gedownload", email_requested: "Verzending aangevraagd", email_result: "Verzendresultaat", email_failed: "Verzending mislukt" }[e.action] || e.action}</span>
            {" · "}{e.at?.replace("T", " ").slice(0, 19)}{e.actor_email && ` · ${e.actor_email}`}
            {e.result && ` · ${{ sent: "Aan mailprovider aangeboden", failed: "Mislukt", skipped_no_key: "Niet verzonden: mailconfiguratie ontbreekt" }[e.result] || e.result}`}
          </li>)}
        </ul>
        {!inv.document_events?.length && <p data-testid="credit-audit-legacy">Oud document: raadpleeg het centrale auditlogboek voor eerdere gebeurtenissen.</p>}
      </details>
    </section>
  );
  if (error) return <p role="alert" className="text-red-700 text-xs" data-testid="credit-relations-error">{error}</p>;
  if (!data?.credit_notes?.length) return null;
  const rows = [
    ["original", "Oorspronkelijke factuur", data.original_total],
    ["credited", "Gecrediteerd", data.total_credited],
    ["received", "Ontvangen betalingen", data.total_paid],
    ["due", "Openstaand", data.amount_due],
    ["customer-credit", "Klanttegoed vóór eventuele terugbetaling", data.customer_credit_before_refunds],
  ];
  return (
    <section className="border-t pt-4 space-y-3 text-xs" data-testid="credit-relation-panel-inv">
      <h4 className="font-bold flex gap-2 items-center"><FileMinus2 className="w-4 h-4" /> Creditnota’s</h4>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {rows.map(([id, label, value]) => <div key={id}><dt className="text-gray-500">{label}</dt><dd className="font-mono font-bold" data-testid={`credit-summary-${id}`}>{money(value, data.currency)}</dd></div>)}
        <div><dt className="text-gray-500">Daadwerkelijke terugbetaling</dt><dd data-testid="credit-refund-status">Niet geregistreerd in dit dossier</dd></div>
      </dl>
      <ul className="divide-y" data-testid="credit-linked-list">
        {data.credit_notes.map(cn => <li key={cn.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
          <Button variant="link" className="p-0 h-auto text-xs whitespace-normal" onClick={() => onOpenInvoice(cn.id)} data-testid={`credit-open-${cn.id}`}>{cn.invoice_number}<ArrowUpRight className="w-3 h-3 ml-1 shrink-0" /></Button>
          <span data-testid={`credit-date-${cn.id}`}>{cn.invoice_date}</span>
          <span className="font-mono text-red-700" data-testid={`credit-amount-${cn.id}`}>{money(cn.total_incl_vat, data.currency)}</span>
        </li>)}
      </ul>
    </section>
  );
};